import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import '@/App.css';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { Lock } from 'lucide-react';
import Layout from '@/components/Layout';
import ChatPanel from '@/components/ChatPanel';

// Pages
import Dashboard from '@/pages/Dashboard';
import RMDashboard from '@/pages/RMDashboard';
import UserDashboard from '@/pages/UserDashboard';
import AdminDashboard from '@/pages/AdminDashboard_v2';
import Chat from '@/pages/Chat';
import AdminClientsProspects from '@/pages/AdminClientsProspects';
import AdminReviews from '@/pages/AdminReviews';
import ReportView from '@/pages/ReportView';
import ComprehensiveQuestionnaire from '@/pages/ComprehensiveQuestionnaire';
import RiskProfileResults from '@/pages/RiskProfileResults';
import LandingPage from '@/pages/LandingPage';
import HomePage from '@/pages/HomePage';
import ClientsProspects from '@/pages/ClientsProspects';
import RiskProfiling from '@/pages/RiskProfiling';
import RiskAssessmentPage from '@/pages/RiskAssessmentPage';
import PortfolioManagement from '@/pages/PortfolioManagement';
import Recommendations from '@/pages/Recommendations';
import PortfolioReview from '@/pages/PortfolioReview';
import FamilyManagement from '@/pages/FamilyManagement';
import EnhancedReports from '@/pages/EnhancedReports';
import AdminFamilyManagement from '@/pages/AdminFamilyManagement';
import AdminEnhancedReports from '@/pages/AdminEnhancedReports';
import SecurityMaster from '@/pages/SecurityMaster';
import AdminSecurityMaster from '@/pages/AdminSecurityMaster';
import MarketOutlook from '@/pages/admin/MarketOutlook';
import AdminRecommendations from '@/pages/admin/Recommendations';
import WorkflowPage from '@/pages/WorkflowPage';
import DeviationHandling from '@/pages/DeviationHandling';
import RMKnowledgeCentre from '@/pages/RMKnowledgeCentre';
import SharePointCallback from '@/pages/SharePointCallback';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Finding 4: Token auth is now via HttpOnly cookie; axios.defaults.withCredentials = true
// is set in AuthContext.js so cookies are sent on every axios request.
// Strip stale null/undefined Bearer tokens left over from localStorage reads.
axios.interceptors.request.use(
  (config) => {
    const auth = config.headers?.Authorization;
    if (auth === 'Bearer null' || auth === 'Bearer undefined' || auth === 'Bearer ') {
      delete config.headers.Authorization;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="loading-shimmer w-16 h-16 rounded-full"></div>
      </div>
    );
  }

  return user ? children : <Navigate to="/" />;
};

const FeatureLockedPage = ({ user, onLogout, featureName }) => {
  const navigate = useNavigate();
  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-6">
          <Lock size={32} className="text-gray-400" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">{featureName} is locked</h2>
        <p className="text-gray-500 mb-6 max-w-md">
          This feature is not available for your account. Please contact Vaia to get access.
        </p>
        <button
          onClick={() => navigate('/dashboard')}
          className="px-4 py-2 bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black rounded-lg transition-colors"
        >
          Back to Dashboard
        </button>
      </div>
    </Layout>
  );
};

function AppRoutes() {
  const { user, loading, signOut } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="loading-shimmer w-16 h-16 rounded-full"></div>
      </div>
    );
  }

  return (
    <>
      {user && <ChatPanel user={user} />}
      <Routes>
        <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            {user?.role === 'admin' ? (
              <AdminDashboard user={user} onLogout={signOut} />
            ) : user?.role === 'rm' ? (
              <RMDashboard user={user} onLogout={signOut} />
            ) : user?.role === 'user' ? (
              <UserDashboard user={user} onLogout={signOut} />
            ) : (
              <Dashboard user={user} onLogout={signOut} />
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/questionnaire"
        element={
          <ProtectedRoute>
            <ComprehensiveQuestionnaire user={user} onLogout={signOut} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/risk-profile-results"
        element={
          <ProtectedRoute>
            <RiskProfileResults user={user} onLogout={signOut} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/clients-prospects"
        element={
          <ProtectedRoute>
            <AdminClientsProspects user={user} onLogout={signOut} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/reviews"
        element={
          <ProtectedRoute>
            <AdminReviews user={user} onLogout={signOut} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/report/:reportId"
        element={
          <ProtectedRoute>
            <ReportView user={user} onLogout={signOut} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients-prospects"
        element={
          <ProtectedRoute>
            <ClientsProspects user={user} onLogout={signOut} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/risk-profiling"
        element={
          <ProtectedRoute>
            <RiskProfiling user={user} onLogout={signOut} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/risk-assessment"
        element={
          <ProtectedRoute>
            <RiskAssessmentPage user={user} onLogout={signOut} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portfolio"
        element={
          <ProtectedRoute>
            <PortfolioManagement user={user} onLogout={signOut} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/recommendations"
        element={
          <ProtectedRoute>
            <Recommendations user={user} onLogout={signOut} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portfolio-review"
        element={
          <ProtectedRoute>
            <PortfolioReview user={user} onLogout={signOut} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/workflow"
        element={
          <ProtectedRoute>
            {user?.disabled_features?.includes('generate_report')
              ? <FeatureLockedPage user={user} onLogout={signOut} featureName="Generate Report" />
              : <WorkflowPage user={user} onLogout={signOut} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/family-management"
        element={
          <ProtectedRoute>
            <FamilyManagement user={user} onLogout={signOut} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/security-master"
        element={
          <ProtectedRoute>
            <SecurityMaster user={user} onLogout={signOut} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/deviation-handling"
        element={
          <ProtectedRoute>
            {user?.disabled_features?.includes('deviation_handling')
              ? <FeatureLockedPage user={user} onLogout={signOut} featureName="Deviation Handling" />
              : <DeviationHandling user={user} onLogout={signOut} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/rm-knowledge-centre"
        element={
          <ProtectedRoute>
            {user?.disabled_features?.includes('knowledge_centre')
              ? <FeatureLockedPage user={user} onLogout={signOut} featureName="RM Knowledge Centre" />
              : <RMKnowledgeCentre user={user} onLogout={signOut} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/family-management"
        element={
          <ProtectedRoute>
            <AdminFamilyManagement user={user} onLogout={signOut} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/security-master"
        element={
          <ProtectedRoute>
            <AdminSecurityMaster user={user} onLogout={signOut} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/market-outlook"
        element={
          <ProtectedRoute>
            <MarketOutlook user={user} onLogout={signOut} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/market-outlook"
        element={
          <ProtectedRoute>
            <MarketOutlook user={user} onLogout={signOut} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/recommendations"
        element={
          <ProtectedRoute>
            <AdminRecommendations user={user} onLogout={signOut} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/enhanced-reports"
        element={
          <ProtectedRoute>
            <AdminEnhancedReports user={user} onLogout={signOut} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat"
        element={
          <ProtectedRoute>
            <Chat user={user} onLogout={signOut} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sharepoint-callback"
        element={
          <ProtectedRoute>
            <SharePointCallback />
          </ProtectedRoute>
        }
      />
      <Route
        path="/login"
        element={<Navigate to="/" replace />}
      />
      <Route
        path="/"
        element={
          user ? <Navigate to="/dashboard" /> : <HomePage />
        }
      />
      </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
